import type { GeoLevel, TimeRangeMap } from '../types';

type PackageIndicators = { x: string; y: string; r: string; c?: string | null };

/**
 * Returns the selected län code if geoFilterIds is a single two-digit län code
 * (the same rule the API uses to filter by län prefix), otherwise null.
 */
export function getSelectedLanCode(geoFilterIds: string[]): string | null {
    if (geoFilterIds.length !== 1) return null;
    return /^\d{2}$/.test(geoFilterIds[0]) ? geoFilterIds[0] : null;
}

/**
 * Returns the län code shared by all geoFilterIds (kommun and lower-level
 * geoIds start with it), or null if they span several län. Used to only fetch
 * indicator data for the län being shown.
 */
export function getFilterLanCode(geoFilterIds: string[]): string | null {
    const lanCodes = new Set(geoFilterIds.map((geoId) => geoId.slice(0, 2)));
    if (lanCodes.size !== 1) return null;
    const [lanCode] = lanCodes;
    return /^\d{2}$/.test(lanCode) ? lanCode : null;
}

export function indicatorCoversLan(
    timeRangeMap: TimeRangeMap,
    indicatorId: string,
    geoLevel: GeoLevel,
    lanCode: string | null
): boolean {
    if (!lanCode) return true;
    const timeRange = timeRangeMap[indicatorId]?.[geoLevel];
    if (!timeRange) return false;
    if (!timeRange.lanTimeRanges) return true;
    return lanCode in timeRange.lanTimeRanges;
}

/**
 * Narrows each time range to the years with data in the given län, so the
 * timeline doesn't start with years that only have data elsewhere. Ranges
 * without data in the län are left as they are.
 */
export function getLanTimeRangeMap(
    timeRangeMap: TimeRangeMap,
    lanCode: string | null
): TimeRangeMap {
    if (!lanCode) return timeRangeMap;
    const result: TimeRangeMap = {};
    for (const [indicatorId, byGeoLevel] of Object.entries(timeRangeMap)) {
        result[indicatorId] = {};
        for (const [geoLevel, range] of Object.entries(byGeoLevel)) {
            const lanTimeRange = range.lanTimeRanges?.[lanCode];
            result[indicatorId][geoLevel] = lanTimeRange
                ? { ...range, timeRange: lanTimeRange }
                : range;
        }
    }
    return result;
}

export function packageCoversLan(
    dataPackage: PackageIndicators,
    timeRangeMap: TimeRangeMap,
    geoLevel: GeoLevel,
    lanCode: string | null
): boolean {
    if (!lanCode) return true;
    return [dataPackage.x, dataPackage.y, dataPackage.r, dataPackage.c]
        .filter((indicatorId): indicatorId is string => Boolean(indicatorId))
        .every((indicatorId) =>
            indicatorCoversLan(timeRangeMap, indicatorId, geoLevel, lanCode)
        );
}

/**
 * "Stockholms" -> "Stockholm", "Gotlands" -> "Gotland", "Skåne" -> "Skåne".
 */
export function formatLanName(name: string): string {
    return name.endsWith('s') ? name.slice(0, -1) : name;
}

export type LanSelectionOutcome<P> =
    | { type: 'keep' }
    | { type: 'switch'; dataPackage: P }
    | { type: 'none' };

/**
 * What selecting a län will do to the shown data: keep the current indicators,
 * switch to the first package with data there, or nothing has data there.
 */
export function getLanSelectionOutcome<
    P extends PackageIndicators & { forceGeolevel?: GeoLevel | null }
>(options: {
    currentIndicators: PackageIndicators | null;
    dataPackages: P[];
    timeRangeMap: TimeRangeMap;
    geoLevel: GeoLevel;
    lanCode: string;
}): LanSelectionOutcome<P> {
    const { currentIndicators, dataPackages, timeRangeMap, geoLevel, lanCode } =
        options;
    if (
        !currentIndicators ||
        packageCoversLan(currentIndicators, timeRangeMap, geoLevel, lanCode)
    ) {
        return { type: 'keep' };
    }
    const dataPackage = dataPackages.find((candidate) =>
        packageCoversLan(
            candidate,
            timeRangeMap,
            candidate.forceGeolevel ?? geoLevel,
            lanCode
        )
    );
    return dataPackage ? { type: 'switch', dataPackage } : { type: 'none' };
}
